import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Box, Button, Input } from '@chakra-ui/react';
import { updateBusiness } from '../../api/business';
import { getSelectedBusiness, saveSelectedBusiness } from '../../utils/businessStorage';
import { toast } from 'react-toastify';
import { usePostHog } from '@posthog/react';

const GeneralTab = () => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [businessName, setBusinessName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const storedBusiness = getSelectedBusiness();
    if (storedBusiness) {
      setBusinessName(storedBusiness.name);
      setOriginalName(storedBusiness.name);
    }
  }, []);

  const hasChanges = businessName.trim() !== originalName;

  const handleSave = async () => {
    const storedBusiness = getSelectedBusiness();
    if (!storedBusiness || !businessName.trim()) return;

    setIsSaving(true);
    try {
      await updateBusiness(storedBusiness.id, { name: businessName.trim() });
      saveSelectedBusiness({
        id: storedBusiness.id,
        name: businessName.trim(),
        is_owner: storedBusiness.is_owner,
        is_onboarding_complete: storedBusiness.is_onboarding_complete,
      });
      setOriginalName(businessName.trim());
      window.dispatchEvent(new CustomEvent('business-updated'));
      posthog?.capture('business_settings_saved', {
        business_id: storedBusiness.id,
      });
      toast.success(t('business.businessUpdated'));
    } catch (error) {
      toast.error(t('business.updateFailed'));
      console.error(error);
      posthog?.captureException(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      backgroundColor="neutral.800"
      borderWidth="1px"
      borderColor="neutral.700"
      borderRadius="8px"
      p="24px"
    >
      <Flex direction="column" gap="24px">
        <Flex direction="column" rowGap="8px">
          <Box
            fontWeight="400"
            fontSize="13px"
            lineHeight="150%"
            color="neutral.400"
          >
            {t('business.businessName')}
          </Box>
          <Input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder={t('business.enterBusinessName')}
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.700"
            borderRadius="8px"
            maxWidth="400px"
            _placeholder={{ color: 'neutral.500' }}
            _focus={{
              borderColor: 'neutral.600',
              outline: 'none',
            }}
          />
        </Flex>

        <Box>
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasChanges || !businessName.trim() || isSaving}
            height="fit-content"
            py="8px"
            px="16px"
            fontWeight="500"
            fontSize="13px"
            color="neutral.900"
            backgroundColor="neutral.100"
            borderRadius="6px"
            _hover={{
              backgroundColor: 'neutral.200',
            }}
            _disabled={{
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
          >
            {t('common.saveChanges')}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default GeneralTab;
